import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Heart } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const PRESETS = [5, 10, 25];

/**
 * TipJar — drop-in donation widget for chef + supporter pages.
 *
 * Props:
 *   recipient: string identifier ('omnimuscle' | 'david_lauer' | supporter_<id>)
 *   recipientName: display name shown to the user
 *   className: optional wrapper class
 *
 * Flow:
 *   1. User picks preset OR types custom amount
 *   2. POST /api/billing/tip → returns Stripe checkout URL
 *   3. Redirect to Stripe Checkout
 *   4. On return to /billing/success the BillingSuccess page polls /api/billing/tip-status
 */
export default function TipJar({ recipient, recipientName, className = "" }) {
  const [amount, setAmount] = useState(PRESETS[0]);
  const [custom, setCustom] = useState("");
  const [message, setMessage] = useState("");
  const [busy, setBusy] = useState(false);

  async function tip() {
    if (busy) return;
    const useCustom = custom !== "";
    const final = useCustom ? parseFloat(custom) : amount;
    if (!final || final < 1 || final > 500) {
      toast.error("Tip must be between $1 and $500.");
      return;
    }
    setBusy(true);
    try {
      const origin_url = window.location.origin;
      const { data } = await axios.post(`${API}/billing/tip`, {
        amount: final,
        recipient,
        message: (message || "").slice(0, 280),
        origin_url,
      });
      if (!data?.url) throw new Error("No checkout URL.");
      window.location.href = data.url;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not start checkout.");
      setBusy(false);
    }
  }

  return (
    <div
      data-testid="tip-jar"
      data-testid-recipient={recipient}
      className={`bg-[#0F0F0F] border-2 border-[#D4AF37] p-5 ${className}`}
    >
      <div className="flex items-center gap-2 mb-3">
        <Heart size={16} className="text-[#D4AF37]" />
        <span className="text-[10px] uppercase tracking-[0.3em] text-[#D4AF37] font-bold">
          Tip Jar
        </span>
      </div>
      <div className="font-display text-xl text-white leading-tight mb-3">
        Send {recipientName} a tip 🔥
      </div>
      <div className="flex items-center gap-2 mb-3 flex-wrap">
        {PRESETS.map((p) => (
          <button
            key={p}
            data-testid={`tip-preset-${p}`}
            onClick={() => { setAmount(p); setCustom(""); }}
            className={
              "px-4 py-2 text-sm font-bold uppercase tracking-widest transition-colors border " +
              (amount === p && custom === ""
                ? "bg-[#D4AF37] text-[#0A0A0A] border-[#D4AF37]"
                : "bg-transparent text-[#D4AF37] border-zinc-800 hover:border-[#D4AF37]")
            }
          >
            ${p}
          </button>
        ))}
        <input
          data-testid="tip-custom-input"
          type="number"
          step="0.01"
          min="1"
          max="500"
          placeholder="Custom $"
          value={custom}
          onChange={(e) => setCustom(e.target.value)}
          className="input-omni w-28 text-sm"
        />
      </div>
      <textarea
        data-testid="tip-message"
        value={message}
        onChange={(e) => setMessage(e.target.value)}
        placeholder="Add a message (optional, ≤280 chars)"
        maxLength={280}
        className="input-omni w-full h-16 resize-none mb-3"
      />
      <button
        onClick={tip}
        disabled={busy}
        data-testid="tip-jar-pay-btn"
        className="w-full bg-[#D4AF37] hover:bg-[#F1C84B] text-[#0A0A0A] font-bold uppercase tracking-[0.2em] text-xs py-3 px-5 transition-colors disabled:opacity-50"
      >
        {busy ? "Opening Stripe…" : `Tip $${custom || amount} →`}
      </button>
      <div className="text-[10px] uppercase tracking-widest text-neutral-600 mt-2 text-center">
        Secure via Stripe · One-time · No account needed
      </div>
    </div>
  );
}
